/**
 * 下单各段的**实测耗时**，给进度条当预期用。
 *
 * ── 为什么要这个东西 ────────────────────────────────────────────────
 *
 * m-booking-progress 的三个里程碑（锁班次 / 下单 / 调起支付）本来就是真事件
 * 驱动的，不假。假的是**里程碑之间那段爬行**：原来写死 `duration: 6`，于是
 * 一段实际只花 800ms 的等待里，条子只爬完 800/6000 = 13% 的额度（而额度本身
 * 又只有档距的 40%）—— 等于只动了档距的 5%，肉眼近似静止，然后里程碑一到
 * 「啪」跳一大格。
 *
 * **「假」的感觉来自这个「不动 → 突跳」的节奏，不是来自数值不准。**
 *
 * 所以这里记下每一段真实花了多久，下次用它驱动爬行速度。网快就跑得快、
 * 网慢就走得稳，里程碑落地时条子正好在附近，没有突跳。
 *
 * 这也是 Chrome / YouTube 那类进度条的做法 —— 它们同样不知道确切进度，
 * 但**速度有数据支撑**，所以读起来真。
 */

/** 可预测的两段。paying 之后是用户在支付面板上的时间，不可预测，不记。 */
export type BookingSegment = 'lock' | 'pay'

const KEY = 'mss_booking_pace_v1'
/** 留最近 N 次。太长会让网络环境变化后半天跟不上，太短又容易被一次抖动带偏。 */
const KEEP = 7

/**
 * 没有历史时的保守默认（ms）。
 * 取保守值而不是乐观值：预期偏长的话条子走得慢一点，里程碑提前到达会往前
 * 补一小段，观感是「比想象中快」；反过来预期偏短会先冲到上限再干等，
 * 那正是我们要消灭的「卡住」。
 */
const DEFAULTS: Record<BookingSegment, number> = {
  lock: 1200,
  pay: 600,
}

/** 明显不是正常样本的值直接丢掉：卡了半天的、以及时钟跳变造成的负数/零。 */
const MIN_SAMPLE = 60
const MAX_SAMPLE = 20000

type Store = Partial<Record<BookingSegment, number[]>>

const read = (): Store => {
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? (parsed as Store) : {}
  } catch {
    // 隐私模式 / 存储被禁 —— 退回默认值即可，不能因此影响下单
    return {}
  }
}

const write = (store: Store) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(store))
  } catch {
    /* 同上，写不进去就算了 */
  }
}

/** 记一次实测耗时 */
export const recordBookingPace = (segment: BookingSegment, ms: number) => {
  if (!Number.isFinite(ms) || ms < MIN_SAMPLE || ms > MAX_SAMPLE) return
  const store = read()
  const list = Array.isArray(store[segment]) ? store[segment]!.slice() : []
  list.push(Math.round(ms))
  store[segment] = list.slice(-KEEP)
  write(store)
}

/**
 * 这一段的预期耗时（ms）。
 *
 * 用**中位数**不用平均：一次超时（比如切后台）会把平均值拉到天上去，
 * 而中位数对这种离群点免疫。
 */
export const bookingPace = (segment: BookingSegment): number => {
  const list = read()[segment]
  if (!Array.isArray(list) || !list.length) return DEFAULTS[segment]
  const sorted = list.slice().sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  const median =
    sorted.length % 2 ? sorted[mid] : Math.round((sorted[mid - 1] + sorted[mid]) / 2)
  return Number.isFinite(median) && median > 0 ? median : DEFAULTS[segment]
}

/**
 * 卡片一出现就给个起点，不从 0 爬 —— 0 起步会有一瞬间「什么都没发生」的空档。
 */
export const HEAD_START = 0.1

/**
 * 可预测部分的终点。之后那一截留给真正的支付。
 *
 * ⚠️ 末尾刻意留出明显余量：paying 只到 0.8，加上蠕动也到不了 1，
 * 剩下的一截要等真的成了才走满。假进度条骗过一次，用户就再也不信第二次 ——
 * 而这条路后面接的是钱。
 *
 * 第一版给到 0.9 + 蠕动上限 0.95，实测那个位置视觉上已经等于「基本完成」，
 * 和这条原则自己打架，所以整体往回收了一档。
 */
export const PAY_TARGET = 0.8

/**
 * 各档位的进度值，按两段的**实测耗时占比**切分 [HEAD_START, PAY_TARGET]。
 *
 * 原来是写死的 0.25 / 0.55 / 0.8。如果实际是「锁班次+下单 900ms、
 * 取支付参数 300ms」，真实占比是 75% / 25%，而条子走到 0.55 时墙钟只过了
 * 46% —— 「走到一半」和「过了一半」对不上，这是它读起来假的一部分。
 *
 * 没有历史时 bookingPace 回保守默认，退化成一组固定值。
 */
export const bookingStepTargets = (): Record<string, number> => {
  const lock = bookingPace('lock')
  const pay = bookingPace('pay')
  const band = PAY_TARGET - HEAD_START
  const share = lock / Math.max(lock + pay, 1)
  return {
    '': 0,
    locking: HEAD_START,
    created: HEAD_START + band * share,
    paying: PAY_TARGET,
    done: 1,
  }
}
